import { Fragment, useMemo, useState, type ReactNode } from 'react'
import {
  Alert, Box, Button, IconButton, InputAdornment, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import { ChevronDown, ChevronRight, Download, LifeBuoy, Network, Search, ShieldAlert, TrendingDown } from 'lucide-react'
import {
  AppShell, Crumb, Flag as CountryFlag, SkeletonCards, Surface, formatDate, formatDateTime, tokens, useNavigate,
} from '@agribot/polygon-checks-ui'
import { useDashboard, useExport, useReviewStore, useStructure } from '../hooks'
import { countryName } from '@agribot/sourcing-model'
import { DiagramDialog } from '../components/TreeDialog'
import { GroupStructure } from '../components/GroupStructure'
import { PlotBar } from '../components/SupplyDrill'
import { DemandPanel } from '../components/DemandPanel'
import { STATUS_LABEL } from '../rules'
import { scenarioLink, stopSuppliers } from '../scenario'
import type { EntityProgress, ExportFormat, ExportLevel, ParentProgress, Stage } from '../types'

const STAGE_LABEL: Record<Stage, string> = {
  invited: 'Invited, not signed in',
  signed_in: 'Signed in',
  started: 'Started',
  submitted: 'Submitted',
  approved: 'Approved',
  returned: 'Returned',
}

const STAGE_TONE: Record<Stage, { bg: string; fg: string }> = {
  invited: { bg: tokens.color.ink100, fg: tokens.color.ink600 },
  signed_in: { bg: tokens.color.ink100, fg: tokens.color.ink700 },
  started: { bg: tokens.color.brandTeal100, fg: '#18576e' },
  submitted: { bg: tokens.color.brandTeal100, fg: '#18576e' },
  approved: { bg: tokens.color.brandGreen100, fg: '#1f4741' },
  returned: { bg: tokens.color.tintWarning, fg: '#7d5a1a' },
}

/**
 * Screen 11: the campaign's progress. A funnel from invited to submitted,
 * then by parent company down to each legal entity, and the people who
 * tried to sign in without an account (usually with a colleague's link).
 */
export function CampaignDashboard() {
  const { data, isPending, error } = useDashboard()
  const store = useReviewStore()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [region, setRegion] = useState('')
  const [open, setOpen] = useState<Set<string>>(new Set())
  const { run: exportRun, job: exportJob } = useExport()
  const [exporting, setExporting] = useState<{ format: ExportFormat; level: ExportLevel } | null>(null)
  const [structureOf, setStructureOf] = useState<ParentProgress | null>(null)

  const entities = useMemo(() => data?.parents.flatMap((p) => p.entities) ?? [], [data])
  const f = data?.funnel ?? { invited: 0, signedIn: 0, started: 0, submitted: 0, approved: 0, returned: 0, inReview: 0 }
  const regions = [...new Set(data?.parents.map((p) => p.region) ?? [])].sort()
  const q = query.trim().toLowerCase()
  const parents = (data?.parents ?? [])
    .filter((p) => !region || p.region === region)
    .map((p) => (q && !p.name.toLowerCase().includes(q) ? { ...p, entities: p.entities.filter((e) => e.name.toLowerCase().includes(q)) } : p))
    .filter((p) => p.entities.length > 0)

  // A scenario for what is on screen: with a search or region narrowing the list, those legal entities stop.
  const filtered = Boolean(q || region)
  const scenarioFor = (supplierIds: string[]) => data && navigate(scenarioLink(data.campaign.id, supplierIds.length ? [stopSuppliers(supplierIds)] : []))
  const runScenario = () => scenarioFor(filtered ? parents.flatMap((p) => p.entities.map((e) => e.id)) : [])

  const download = (format: ExportFormat, level: ExportLevel = 'origins') => {
    setExporting({ format, level })
    exportRun.mutate({ format, level }, { onSettled: () => setExporting(null) })
  }
  const preparing = (format: ExportFormat, level: ExportLevel) =>
    exporting?.format === format && exporting.level === level
      ? exportJob?.status === 'running' ? 'Building…' : 'Preparing…'
      : null

  return (
    <AppShell
      crumbs={[<Crumb key="a">Sourcing</Crumb>, <Crumb key="b">Dashboard</Crumb>]}
      actions={(
        <Box sx={{ display: 'flex', gap: 2 }}>
          {data && (
            <Tooltip describeChild title={filtered ? 'Opens Scenarios for this campaign, with the legal entities shown here stopped' : 'Opens Scenarios for this campaign'}>
              <Button variant="outlined" startIcon={<TrendingDown size={16} />} onClick={runScenario}>Run a scenario</Button>
            </Tooltip>
          )}
          {(['xlsx', 'csv'] as const).filter((fmt) => store.exportFormats.includes(fmt)).map((fmt) => (
            <Button key={fmt} variant="outlined" startIcon={<Download size={16} />} disabled={exporting !== null} onClick={() => download(fmt)}>
              {preparing(fmt, 'origins') ?? (fmt === 'xlsx' ? 'Export to Excel' : 'Export to CSV')}
            </Button>
          ))}
          {store.exportLevels.includes('plots') && store.exportFormats.includes('xlsx') && (
            <Tooltip title="One row per plot behind each origin: farmer, cooperative, the plot's check, its declared production and the tonnes it accounts for">
              <span>
                <Button variant="outlined" startIcon={<Download size={16} />} disabled={exporting !== null} onClick={() => download('xlsx', 'plots')}>
                  {preparing('xlsx', 'plots') ?? 'Export with farms'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      )}
    >
      <Box sx={{ flex: 1, overflow: 'auto', px: 8, py: 7 }}>
        {isPending && <SkeletonCards columns="repeat(4, 1fr)" />}
        {error && <Alert severity="error">{(error as Error).message}</Alert>}
        {exportRun.isError && <Alert severity="error" sx={{ mb: 4 }} onClose={() => exportRun.reset()}>{(exportRun.error as Error).message}</Alert>}
        {data && (
          <>
            <Typography variant="h1" sx={{ mb: 2 }}>{data.campaign.name}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 7 }}>
              Open {formatDate(data.campaign.opensAt)} to {formatDate(data.campaign.closesAt)} · {entities.length} legal entities in {data.parents.length} parent companies
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 4, mb: 4 }}>
              <Step label="Invited" value={f.invited} of={f.invited} />
              <Step label="Signed in" value={f.signedIn} of={f.invited} />
              <Step label="Started" value={f.started} of={f.invited} />
              <Step label="Submitted" value={f.submitted} of={f.invited} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 4, mb: 8 }}>
              <Outcome label="In review" value={f.inReview} />
              <Outcome label="Approved" value={f.approved} tone="success" />
              <Outcome label="Returned to supplier" value={f.returned} tone="warning" />
              <Outcome label="Access issues" value={data.accessIssues.length} tone={data.accessIssues.length ? 'danger' : undefined} />
              <Outcome
                label="Supplier requests"
                value={data.supplierRequests.open}
                detail={data.supplierRequests.answered ? `${data.supplierRequests.answered} answered, still open` : undefined}
                tone={data.supplierRequests.open ? 'warning' : undefined}
                icon={<LifeBuoy size={16} />}
                onClick={() => navigate('/sourcing/requests')}
              />
            </Box>

            <Box sx={{ mb: 8 }}><DemandPanel campaignId={data.campaign.id} editable={false} compact /></Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 6, alignItems: 'start' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                  <Typography variant="h3" component="h2" sx={{ flex: 1 }}>By parent company</Typography>
                  <TextField
                    placeholder="Search companies"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} /></InputAdornment> }}
                    inputProps={{ 'aria-label': 'Search companies' }}
                    sx={{ width: 240 }}
                  />
                  <TextField select value={region} onChange={(e) => setRegion(e.target.value)} sx={{ width: 180, '& .MuiSelect-select': { minHeight: 32, boxSizing: 'border-box', display: 'flex', alignItems: 'center', py: 0 } }} SelectProps={{ displayEmpty: true }} inputProps={{ 'aria-label': 'Region' }}>
                    <MenuItem value="">All regions</MenuItem>
                    {regions.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                  </TextField>
                </Box>
                <Surface sx={{ overflowX: 'auto' }}>
                  <Table sx={{
                    minWidth: 820, '& td, & th': { whiteSpace: 'nowrap' },
                    // "What if this stopped?" stays in view while the table scrolls sideways.
                    '& tr > :last-child': {
                      position: 'sticky', right: 0, zIndex: 1, backgroundColor: 'background.paper',
                      boxShadow: `inset 1px 0 0 ${tokens.color.ink100}`,
                    },
                  }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 40 }} />
                        <TableCell sx={{ width: '36%' }}>Company</TableCell>
                        <TableCell>Stage</TableCell>
                        <TableCell sx={{ width: 160 }}>Materials</TableCell>
                        <TableCell>Submitted</TableCell>
                        <TableCell>EUDR plots</TableCell>
                        <TableCell>Last activity</TableCell>
                        <TableCell sx={{ width: 48 }} aria-label="What if this stopped?" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parents.map((p) => {
                        const expanded = open.has(p.id) || Boolean(q)
                        return (
                          <Fragment key={p.id}>
                            <ParentRow
                              parent={p}
                              expanded={expanded}
                              onWhatIf={() => scenarioFor(p.entities.map((e) => e.id))}
                              onStructure={isGroup(p) && p.entities.some((e) => e.submissionId) ? () => setStructureOf(p) : undefined}
                              onToggle={() => setOpen((prev) => {
                                const next = new Set(prev)
                                if (next.has(p.id)) next.delete(p.id)
                                else next.add(p.id)
                                return next
                              })}
                            />
                            {expanded && p.entities.map((e) => (
                              <EntityRow key={e.id} entity={e} onWhatIf={() => scenarioFor([e.id])} onOpen={e.submissionId ? () => navigate(`/sourcing/reviews/${e.submissionId}`) : undefined} />
                            ))}
                          </Fragment>
                        )
                      })}
                    </TableBody>
                  </Table>
                </Surface>
              </Box>

              <Surface sx={{ p: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <ShieldAlert size={18} strokeWidth={1.75} color={tokens.color.danger} />
                  <Typography variant="h4" component="h2">Access issues</Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
                  People who tried to sign in without an account, usually with a link a colleague forwarded. Invite them, or tell the supplier who is already set up.
                </Typography>
                {data.accessIssues.length === 0 && <Typography variant="body2">Nobody so far.</Typography>}
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                  {data.accessIssues.map((a) => (
                    <Box component="li" key={a.email} sx={{ py: 3, borderTop: `1px solid ${tokens.color.ink200}` }}>
                      <Box sx={{ font: `500 13px ${tokens.font.sans}`, wordBreak: 'break-all' }}>{a.email}</Box>
                      <Box sx={{ font: `400 12px ${tokens.font.sans}`, color: 'text.secondary', mt: 0.5 }}>
                        {formatDateTime(a.at)} · {a.attempts} attempt{a.attempts === 1 ? '' : 's'}
                        {a.likelyEntity ? ` · probably ${a.likelyEntity}` : ' · no matching supplier'}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Surface>
            </Box>
            <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: 4 }}>
              Exports hold every submitted answer, one row per origin, with who changed each value and when.
            </Typography>
          </>
        )}
      </Box>
      {structureOf && <StructureDialog parent={structureOf} onClose={() => setStructureOf(null)} />}
    </AppShell>
  )
}

/** A supplier without a parent company is listed on its own, under its own id: there is no group to draw. */
const isGroup = (p: ParentProgress) => !(p.entities.length === 1 && p.entities[0]!.id === p.id)

function StructureDialog({ parent, onClose }: { parent: ParentProgress; onClose: () => void }) {
  const { data, error, isPending } = useStructure(parent.id)
  return (
    <DiagramDialog title={`${parent.name}: legal entities, materials and origins`} fileName={`${parent.name}-structure`} onClose={onClose}>
      {(ref) => (
        <>
          {isPending && <Box sx={{ p: 6 }}><SkeletonCards columns="1fr" /></Box>}
          {error && <Alert severity="error" sx={{ m: 4 }}>{(error as Error).message}</Alert>}
          {data && <GroupStructure ref={ref} structure={data} countryName={(code) => countryName(code, 'en')} />}
        </>
      )}
    </DiagramDialog>
  )
}

function Step({ label, value, of }: { label: string; value: number; of: number }) {
  const share = of ? Math.round((value / of) * 100) : 0
  return (
    <Surface sx={{ p: 5 }}>
      <Typography variant="overline" component="div" sx={{ color: 'text.disabled', mb: 2 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 3 }}>
        <Box sx={{ font: `600 28px ${tokens.font.sans}`, fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
        <Box sx={{ font: `400 13px ${tokens.font.sans}`, color: 'text.secondary' }}>{share}% of invited</Box>
      </Box>
      <Box sx={{ height: 6, borderRadius: 999, backgroundColor: tokens.color.ink100, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${share}%`, backgroundColor: tokens.color.brandTeal }} />
      </Box>
    </Surface>
  )
}

function Outcome({ label, value, tone, detail, icon, onClick }: {
  label: string; value: number; tone?: 'success' | 'warning' | 'danger'; detail?: string; icon?: ReactNode; onClick?: () => void
}) {
  const colour = tone === 'success' ? tokens.color.success : tone === 'warning' ? tokens.color.warning : tone === 'danger' ? tokens.color.danger : 'text.primary'
  return (
    <Surface
      {...(onClick ? { component: 'button', type: 'button', onClick } : {})}
      sx={{
        px: 5, py: 4, display: 'flex', flexDirection: 'column', gap: 1, textAlign: 'left', border: 0, font: 'inherit', width: '100%',
        ...(onClick ? { cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.ink50 }, '&:focus-visible': { boxShadow: tokens.shadow.ring } } : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 3 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>{icon}{label}</Typography>
        <Box sx={{ font: `600 22px ${tokens.font.sans}`, fontVariantNumeric: 'tabular-nums', color: colour }}>{value}</Box>
      </Box>
      {detail && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{detail}</Typography>}
    </Surface>
  )
}

function Progress({ done, total }: { done: number; total: number }) {
  const share = total ? (done / total) * 100 : 0
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ width: 80, flex: 'none', height: 6, borderRadius: 999, backgroundColor: tokens.color.ink100, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${share}%`, backgroundColor: share === 100 ? tokens.color.brandGreen700 : tokens.color.brandTeal }} />
      </Box>
      <Box sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 40, textAlign: 'right' }}>{done}/{total}</Box>
    </Box>
  )
}

/** Plots behind the entity's origins, by EUDR check result. */
function EudrCell({ eudr }: { eudr: EntityProgress['eudr'] }) {
  if (!eudr) return <Box component="span" sx={{ color: 'text.secondary' }}>No farms linked</Box>
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Tooltip title={eudr.productionByCrop ? Object.entries(eudr.productionByCrop).map(([crop, t]) => `${crop}: ${Math.round(t).toLocaleString('en-GB')} t`).join(' · ') : ''}>
        <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {eudr.plots.toLocaleString('en-GB')} plots
          {eudr.productionT ? <Box component="span" sx={{ color: 'text.secondary' }}> · {Math.round(eudr.productionT).toLocaleString('en-GB')} t a year</Box> : null}
        </Box>
      </Tooltip>
      <PlotBar passed={eudr.passed} flagged={eudr.flagged} rejected={eudr.rejected} total={eudr.plots} width={80} />
    </Box>
  )
}

function StagePill({ entity }: { entity: EntityProgress }) {
  const tone = STAGE_TONE[entity.stage]
  const label = entity.stage === 'submitted' && entity.status ? STATUS_LABEL[entity.status] : STAGE_LABEL[entity.stage]
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 22, px: 2, borderRadius: 999, backgroundColor: tone.bg, color: tone.fg, font: `500 12px ${tokens.font.sans}`, whiteSpace: 'nowrap' }}>
      {label}
    </Box>
  )
}

/** "What if this stopped?": opens Scenarios with this company's supply stopped. */
function WhatIf({ name, onClick }: { name: string; onClick: () => void }) {
  return (
    <Tooltip title="What if this stopped?">
      <IconButton size="small" aria-label={`What if ${name} stopped?`} onClick={(e) => { e.stopPropagation(); onClick() }} sx={{ color: tokens.color.ink500 }}>
        <TrendingDown size={15} />
      </IconButton>
    </Tooltip>
  )
}

function ParentRow({ parent, expanded, onToggle, onStructure, onWhatIf }: { parent: ParentProgress; expanded: boolean; onToggle: () => void; onStructure?: () => void; onWhatIf: () => void }) {
  const f = parent.funnel
  const done = parent.materialsComplete
  const total = parent.materialsTotal
  const last = parent.lastActivity
  return (
    <TableRow hover onClick={onToggle} sx={{ cursor: 'pointer', '& td': { height: 56 } }}>
      <TableCell>
        <IconButton size="small" aria-label={expanded ? `Hide ${parent.name}'s entities` : `Show ${parent.name}'s entities`} aria-expanded={expanded} onClick={(e) => { e.stopPropagation(); onToggle() }}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </IconButton>
      </TableCell>
      <TableCell>
        <Box sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'normal' }}>{parent.name}</Box>
        <Box sx={{ color: 'text.secondary', fontSize: 12, display: 'flex', alignItems: 'center', gap: 2 }}>
          {parent.region} · {parent.entities.length} legal entit{parent.entities.length === 1 ? 'y' : 'ies'}
          {onStructure && (
            <Box component="button" type="button" onClick={(e) => { e.stopPropagation(); onStructure() }}
              sx={{ border: 0, background: 'none', p: 0, cursor: 'pointer', color: tokens.color.brandTeal, font: `500 12px ${tokens.font.sans}`, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Network size={12} /> Group structure
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell>{f.submitted} of {f.invited} submitted</TableCell>
      <TableCell><Progress done={done} total={total} /></TableCell>
      <TableCell />
      {/* The API's group figure counts each plot once; adding entities up could count a shared cooperative twice. */}
      <TableCell><EudrCell eudr={parent.eudr ?? null} /></TableCell>
      <TableCell sx={{ color: 'text.secondary' }}>{last ? formatDate(last) : '—'}</TableCell>
      <TableCell sx={{ width: 48 }}><WhatIf name={parent.name} onClick={onWhatIf} /></TableCell>
    </TableRow>
  )
}

function EntityRow({ entity, onOpen, onWhatIf }: { entity: EntityProgress; onOpen?: () => void; onWhatIf: () => void }) {
  return (
    <TableRow sx={{ backgroundColor: tokens.color.ink50, '& td': { height: 52 }, '& > :last-child': { backgroundColor: tokens.color.ink50 } }}>
      <TableCell />
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 2 }}>
          <CountryFlag code={entity.countryCode} size={12} />
          {onOpen ? (
            <Box component="button" type="button" onClick={onOpen} sx={{ border: 0, background: 'none', p: 0, cursor: 'pointer', color: tokens.color.brandTeal, font: `500 13px ${tokens.font.sans}`, textAlign: 'left' }}>
              {entity.name}
            </Box>
          ) : <Box component="span">{entity.name}</Box>}
        </Box>
      </TableCell>
      <TableCell><StagePill entity={entity} /></TableCell>
      <TableCell><Progress done={entity.materialsComplete} total={entity.materialsTotal} /></TableCell>
      <TableCell>{entity.submittedAt ? formatDate(entity.submittedAt) : '—'}</TableCell>
      <TableCell><EudrCell eudr={entity.eudr ?? null} /></TableCell>
      <TableCell sx={{ color: 'text.secondary' }}>{entity.lastActivity ? formatDate(entity.lastActivity) : 'Never'}</TableCell>
      <TableCell sx={{ width: 48 }}><WhatIf name={entity.name} onClick={onWhatIf} /></TableCell>
    </TableRow>
  )
}
